import { defineComponent, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { usePermissionsStore } from '@/store/permissions.store';
import { PS_MANAGEMENT_PERMISSIONS } from '@/models/app-internal/permissions.model';
import {
  UnavailableHoursConfigItemModel,
  AVAILABILITY_ACTIVE_SWITCH_TOOLTIP_TEXT,
  AVAILABILITY_INFO_BANNER_TEXT,
  UnavailableHoursActiveLabel,
} from '@/models/ps-management/availability-hours.model';
import { TagComponentTypes } from '@/models/app-common.model';
import {
  unavailableHoursSettingsFormRef,
  usePaymentMethodsAvailabilityHoursStore,
} from '@/store/ps-management/availability-hours.store';
import { useDayOfWeekStore } from '@/store/dictionary/day-of-week.store';
import { Plus, Close, InfoFilled } from '@element-plus/icons-vue';
import { DayOfWeekItem, WeekDayEnum } from '@/models/dictionary/day-of-week.model';
import { useAvailabilityHoursRules } from '@/hooks/ps-management/availability-hours-validation.rules';

const icons = {
  Plus,
  Close,
  InfoFilled,
};

export default defineComponent({
  name: 'UnavailableHoursSettings',
  emits: ['updatePaymentMethodList'],
  setup(_, { emit }) {
    const paymentMethodsUnavailableHoursStore = usePaymentMethodsAvailabilityHoursStore();
    const {
      currentPaymentMethod,
      availabilityHoursFormData,
      isAvailabilityHoursDataLoading,
      isAvailabilityHoursSettingsSidebarVisible,
      unavailableHoursConfigList,
      isUnavailableSettingsFormChanged,
    } = storeToRefs(paymentMethodsUnavailableHoursStore);
    const {
      removeUnavailableHoursConfigItem,
      addUnavailableHoursConfigItem,
      updatePaymentMethodAvailabilityHours,
      closeAvailabilityHoursSettings,
      getTimeRange,
      updateTimeRange,
    } = paymentMethodsUnavailableHoursStore;

    const { psManagementPermissions } = storeToRefs(usePermissionsStore());

    const dayOfWeekStore = useDayOfWeekStore();
    const { getDayOfWeekList } = dayOfWeekStore;
    const { dayOfWeekList } = storeToRefs(dayOfWeekStore);

    const { availabilityHoursFormRules } = useAvailabilityHoursRules();

    const unavailableHoursActivityLabel = computed<string>(() =>
      availabilityHoursFormData.value.isUnavailableHoursActive
        ? UnavailableHoursActiveLabel.ON
        : UnavailableHoursActiveLabel.OFF,
    );

    const availableDays = computed<DayOfWeekItem[]>(() => {
      const selectedDays = unavailableHoursConfigList.value
        .flatMap((availabilityConfig: UnavailableHoursConfigItemModel) => availabilityConfig.dayList);

      return dayOfWeekList.value.filter((day: DayOfWeekItem) => !selectedDays.includes(day.dayOfWeek));
    });

    const isEmptyOrInvalidBlocks = computed<boolean>(() => {
      return unavailableHoursConfigList.value?.some((unavailableHoursConfig: UnavailableHoursConfigItemModel) => {
        if (!unavailableHoursConfig.dayList || unavailableHoursConfig.dayList.length === 0) {
          return true;
        }

        if (!unavailableHoursConfig.time?.from || !unavailableHoursConfig.time?.to) {
          return true;
        }

        return false;
      });
    });

    const selectedDays = computed<WeekDayEnum[]>(() =>
      unavailableHoursConfigList.value
        .flatMap((unavailableHoursConfig: UnavailableHoursConfigItemModel) => unavailableHoursConfig.dayList),
    );

    const isAllDaysUsed = computed<boolean>(() =>
      selectedDays.value.length >= dayOfWeekList.value.length,
    );

    const isAddUnavailableHoursButtonActive = computed<boolean>(() =>
      availabilityHoursFormData.value.isUnavailableHoursActive &&
      !isAllDaysUsed.value &&
      !isEmptyOrInvalidBlocks.value,
    );

    const isUpdateUnavailableHoursAvailable = computed<boolean>(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_PAYMENT_METHOD_UNAVAILABLE_HOURS_SETTINGS],
    );

    const updateUnavailableHoursSettings = async (): Promise<void> => {
      updatePaymentMethodAvailabilityHours()
        .then(() => {
          emit('updatePaymentMethodList');
          closeAvailabilityHoursSettings();
        });
    };

    onMounted(() => {
      getDayOfWeekList();
    });

    return {
      icons,
      isAvailabilityHoursSettingsSidebarVisible,
      availabilityHoursFormData,
      isAvailabilityHoursDataLoading,
      currentPaymentMethod,
      isAddUnavailableHoursButtonActive,
      TagComponentTypes,
      closeAvailabilityHoursSettings,
      unavailableHoursConfigList,
      unavailableHoursActivityLabel,
      dayOfWeekList,
      updateUnavailableHoursSettings,
      removeUnavailableHoursConfigItem,
      addUnavailableHoursConfigItem,
      availableDays,
      AVAILABILITY_ACTIVE_SWITCH_TOOLTIP_TEXT,
      AVAILABILITY_INFO_BANNER_TEXT,
      availabilityHoursFormRules,
      isEmptyOrInvalidBlocks,
      isUpdateUnavailableHoursAvailable,
      getTimeRange,
      updateTimeRange,
      unavailableHoursSettingsFormRef,
      isUnavailableSettingsFormChanged,
    };
  },
});
